import React from 'react';

const sections = [
  {
    title: '1. Scope of This Privacy Policy',
    paragraphs: [
      'This Privacy Policy applies to personal information collected by CheckInCare through our public-facing website, caregiver and account interfaces, connected device and related cloud services, SMS and email communications, customer support interactions, and automatically collected online data, including cookies and similar technologies.'
    ]
  },
  {
    title: '2. Information We Collect',
    bullets: [
      'Personal Information: name, email address, mobile or telephone number, mailing or billing address if provided, and other contact details you choose to provide',
      'Account Information: username or login credentials, account role or profile information, subscription status, billing-related information, preferences, notification settings, caregiver assignments, customer service records, and account history',
      'Device Data: device identifiers, activation data, status information, check-in activity, missed check-in events, connectivity status, firmware or configuration data, diagnostics, and troubleshooting data',
      'Communication Data: SMS notification logs, email notification logs, message delivery status, timestamps, response data, opt-in, consent, and opt-out records, and customer support communications',
      'Automatically Collected Data: IP address, browser type and version, device type, operating system, referring URLs, pages viewed, usage activity, access dates and times, cookie identifiers, and log data'
    ]
  },
  {
    title: '3. Sources of Information',
    paragraphs: [
      'We collect personal information directly from you, from caregivers, account administrators, or authorized representatives, from the connected device and related cloud systems, from communications sent through or in response to the Services, automatically through cookies and analytics, and from service providers that help us process payments, host infrastructure, send communications, or support operations.'
    ]
  },
  {
    title: '4. How We Use Information',
    bullets: [
      'Providing, operating, and maintaining the Services',
      'Registering, activating, authenticating, and administering accounts and devices',
      'Recording check-ins and processing missed check-in events',
      'Sending alerts, reminders, service notifications, SMS messages, and emails',
      'Managing caregiver notification settings and contact workflows',
      'Providing customer support and responding to inquiries',
      'Processing subscriptions, billing, and payments',
      'Monitoring, troubleshooting, securing, and improving device and service performance',
      'Conducting internal analytics, reporting, quality assurance, and product improvement',
      'Complying with applicable laws, regulations, legal process, and governmental requests'
    ]
  },
  {
    title: '5. SMS Messaging and Text Message Data',
    paragraphs: [
      'CheckInCare collects and uses mobile phone numbers to provide SMS notifications and related service communications, including check-in alerts, missed check-in notifications, service-related messages, and account communications where applicable.',
      'Where required, we collect, maintain, and store records of SMS consent and related messaging preferences.',
      'Text messaging originator opt-in data, SMS consent, and related messaging consent records are not sold, rented, transferred, shared, or otherwise disclosed to third parties for their own marketing or promotional purposes.',
      'The foregoing specifically excludes text messaging originator opt-in data and consent; this information will not be shared with any third parties except vendors and service providers that support message delivery on our behalf and are contractually restricted from using it for any purpose other than providing services to CheckInCare.',
      'SMS messaging and related data may be processed by service providers that help us deliver communications, including Twilio and other communications, hosting, and infrastructure vendors.',
      'You may opt out of SMS messages at any time by replying STOP to any CheckInCare text message. For help with SMS messages, reply HELP where that functionality is available.',
      'Message frequency varies. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.'
    ]
  },
  {
    title: '6. How We Share Information',
    bullets: [
      'With vendors and service providers that perform services on our behalf, including providers of SMS and communications services, email delivery, cloud hosting and storage, payment processing, analytics, monitoring, customer support, security, fraud prevention, and technical operations',
      'With designated caregivers, account administrators, or other recipients selected or authorized through your account settings in order to deliver the Services',
      'When required to comply with applicable law, regulation, subpoena, court order, or legal process, or to protect rights, property, or safety',
      'In connection with an actual or proposed merger, acquisition, financing, asset sale, bankruptcy, reorganization, or similar corporate transaction'
    ]
  },
  {
    title: '7. No Sale or Sharing for Cross-Context Behavioral Advertising',
    paragraphs: [
      'CheckInCare does not sell personal information for money.',
      'CheckInCare also does not knowingly share personal information for cross-context behavioral advertising as those terms are defined under California law.'
    ]
  },
  {
    title: '8. California Privacy Rights (CCPA/CPRA)',
    bullets: [
      'Right to know the categories and specific pieces of personal information we collected about you, the categories of sources, the business or commercial purposes, and the categories of third parties to whom we disclosed personal information',
      'Right to delete personal information we collected from you, subject to exceptions permitted by law',
      'Right to correct inaccurate personal information we maintain about you',
      'Right to opt out of the sale or sharing of your personal information',
      'Right to limit use and disclosure of sensitive personal information where applicable',
      'Right to non-discrimination for exercising your privacy rights'
    ]
  },
  {
    title: '9. How to Exercise Your Privacy Rights',
    paragraphs: [
      'To submit a privacy rights request, you may contact CheckInCare by email at privacy@checkincare.com. Please specify the nature of your request, such as a request to know, delete, or correct your information.',
      'We may need to verify your identity before fulfilling your request. You may designate an authorized agent to make a request on your behalf where permitted by law.'
    ]
  },
  {
    title: '10. Do Not Sell or Share My Personal Information',
    paragraphs: [
      'California residents have the right to opt out of the sale or sharing of their personal information.',
      'CheckInCare does not sell personal information and does not share personal information for cross-context behavioral advertising. As a result, we do not currently provide a separate opt-out mechanism for sale or sharing because those activities are not part of our current practices.'
    ]
  },
  {
    title: '11. Cookies and Tracking Technologies',
    paragraphs: [
      'We and our service providers may use cookies, pixels, local storage, analytics tools, log files, and similar technologies to collect information automatically when you use our website or interfaces.',
      'We may use these technologies to operate and secure the Services, remember preferences and settings, authenticate users, measure website and service usage, improve functionality and performance, diagnose technical issues, and analyze trends and interactions.'
    ]
  },
  {
    title: '12. Data Retention',
    paragraphs: [
      'We retain personal information for as long as reasonably necessary for the purposes described in this Privacy Policy, including to provide and maintain the Services, support device functionality and caregiver notifications, maintain account records, process transactions and subscriptions, preserve records of consent and communications, resolve disputes, and comply with legal and operational obligations.'
    ]
  },
  {
    title: '13. Data Security',
    paragraphs: [
      'CheckInCare uses reasonable administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, disclosure, alteration, and destruction.',
      'However, no system, device, software, transmission method, or storage platform can be guaranteed to be completely secure, and we do not guarantee absolute security.'
    ]
  },
  {
    title: '14. Children\'s Privacy',
    paragraphs: [
      'The Services are not directed to children under the age of 13, and CheckInCare does not knowingly collect personal information directly from children under 13 through the public-facing website or Services.'
    ]
  },
  {
    title: '15. Third-Party Services',
    paragraphs: [
      'The Services rely on third-party vendors and infrastructure providers to support operations, communications, and functionality, including Twilio, email delivery services, cloud hosting and storage providers, payment processors, analytics providers, customer support tools, and security vendors.'
    ]
  },
  {
    title: '16. International Users',
    paragraphs: [
      'CheckInCare is based in the United States, and the Services are operated from and supported in the United States. If you access or use the Services from outside the United States, your information may be transferred to, stored in, and processed in the United States and other jurisdictions where our service providers operate.'
    ]
  },
  {
    title: '17. Changes to This Privacy Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time to reflect changes in our Services, legal obligations, operational practices, or privacy practices. When we make changes, we will post the updated Privacy Policy on our website and update the "Last Updated" date.'
    ]
  },
  {
    title: '18. Contact Information',
    paragraphs: [
      'If you have questions, requests, or concerns about this Privacy Policy or CheckInCare\'s privacy practices, you may contact us at privacy@checkincare.com.'
    ]
  },
  {
    title: '19. Notice of Categories of Personal Information Collected and Disclosed',
    bullets: [
      'Identifiers, such as name, email address, phone number, IP address, account username, and device identifiers',
      'Customer records information, such as billing or subscription-related details',
      'Internet or other electronic network activity information, such as logs, browser data, and usage information',
      'Geolocation or device-related information to the extent reflected in technical or network data',
      'Audio, electronic, or similar information contained in support interactions or communication logs',
      'Inferences drawn from collected information for service operations and improvement'
    ]
  },
  {
    title: '20. California Shine the Light',
    paragraphs: [
      'To the extent California Civil Code Section 1798.83 applies, California residents may request information regarding certain disclosures of personal information to third parties for their own direct marketing purposes. CheckInCare does not disclose personal information to third parties for their own direct marketing purposes.'
    ]
  },
  {
    title: '21. Additional SMS Privacy Notice',
    bullets: [
      'We collect phone numbers for the purpose of sending service-related SMS notifications and related communications',
      'We store records of messaging consent where applicable',
      'We use service providers such as Twilio to process and deliver text messages on our behalf',
      'SMS consent is not shared with third parties for their own marketing purposes',
      'Text messaging originator opt-in data and consent will not be sold or shared with any third parties except vendors acting on our behalf to deliver messaging services',
      'Recipients may opt out of SMS by replying STOP',
      'Message frequency varies. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.'
    ]
  }
];

function Section({ title, paragraphs = [], bullets = [] }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-secondary-900">{title}</h2>
      {paragraphs.map((paragraph) => (
        <p key={paragraph} className="text-sm leading-6 text-secondary-600">
          {paragraph}
        </p>
      ))}
      {bullets.length ? (
        <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-secondary-600">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 px-4 py-10">
      <div className="mx-auto max-w-4xl rounded-2xl bg-white p-8 shadow-xl">
        <h1 className="text-3xl font-bold text-secondary-900">Privacy Policy</h1>
        <p className="mt-3 text-sm font-medium text-secondary-500">Last Updated: April 2026</p>
        <p className="mt-4 text-sm leading-6 text-secondary-600">
          This Privacy Policy describes how CheckInCare collects, uses, discloses, retains,
          and protects personal information when you access or use the Services.
        </p>

        <div className="mt-8 space-y-7">
          {sections.map((section) => (
            <Section key={section.title} {...section} />
          ))}
        </div>
      </div>
    </div>
  );
}
